import type { Point } from './camera';

export type WheelInput = { deltaX: number; deltaY: number; deltaMode?: number; ctrlKey: boolean; x: number; y: number };
export type WheelIntent = { kind: 'pan'; dx: number; dy: number } | { kind: 'zoom'; factor: number; at: Point };

const LINE_PX = 16;
const MAX_STEP = 2;

/**
 * Mouse wheel / trackpad: ctrl+wheel (also what a trackpad pinch sends) zooms around the pointer, plain wheel pans.
 * `pan` is a screen-space content translation (apply with `panBy`).
 */
export function wheelToIntent(e: WheelInput): WheelIntent {
  const unit = e.deltaMode === 1 ? LINE_PX : 1;
  if (e.ctrlKey) {
    const factor = Math.min(MAX_STEP, Math.max(1 / MAX_STEP, Math.exp(-e.deltaY * unit * 0.01)));
    return { kind: 'zoom', factor, at: { x: e.x, y: e.y } };
  }
  return { kind: 'pan', dx: -e.deltaX * unit, dy: -e.deltaY * unit };
}

export type PinchUpdate = { zoomFactor: number; at: Point; pan: Point };

/** Two-finger pinch/pan. Only the first two pointers count; extra fingers are ignored. */
export class PinchTracker {
  private active = new Map<number, Point>();
  private ignored = new Set<number>();

  down(id: number, p: Point): void {
    if (this.active.size >= 2) this.ignored.add(id);
    else this.active.set(id, p);
  }

  /** Apply as: camera = zoomAt(camera, at, zoomFactor), then panBy(camera, pan.x, pan.y). */
  move(id: number, p: Point): PinchUpdate | null {
    const prev = this.active.get(id);
    if (!prev || this.active.size !== 2) {
      if (prev) this.active.set(id, p);
      return null;
    }
    const other = [...this.active.entries()].find(([k]) => k !== id)![1];
    const d0 = Math.hypot(prev.x - other.x, prev.y - other.y);
    const d1 = Math.hypot(p.x - other.x, p.y - other.y);
    const mid0 = { x: (prev.x + other.x) / 2, y: (prev.y + other.y) / 2 };
    const mid1 = { x: (p.x + other.x) / 2, y: (p.y + other.y) / 2 };
    this.active.set(id, p);
    return { zoomFactor: d0 > 0 ? d1 / d0 : 1, at: mid0, pan: { x: mid1.x - mid0.x, y: mid1.y - mid0.y } };
  }

  up(id: number): void {
    this.active.delete(id);
    this.ignored.delete(id);
  }
}
